// C1: this quote is showing the example lines the app ships with.
//
// WHAT THIS IS FOR. The 25 quotes in mocks/quotes.ts have no material list of
// their own, so api.getMaterials hands all of them the same 180-row example
// array. Nothing on screen said so: the lines rendered, priced and edited
// exactly like a real quote's, and the autosave wrote them to the database.
//
// ⚠️ IT MUST BE ON SCREEN BEFORE ANYONE REACHES FOR SAVE. The two banners
// beside it describe conditions a user created and can fix, and either could
// reasonably first be reported at the moment of a press. This one is a
// property of WHICH QUOTE IS OPEN and is true from the first render -- so a
// refusal that waited for a press would let someone build a whole quote on
// example data before finding out none of it could be kept.
//
// ⚠️ DERIVED, NOT LATCHED -- same as UnpricedBanner and FreightBasisBanner.
// Adding materials to one of these quotes forks it a list of its own, at
// which point the quote becomes real and this must disappear by itself.
// QuoteDetail recomputes the problem on every render rather than setting a
// flag, so "it clears when fixed" is structural.
//
// ⚠️ INFORMATIONAL TONE, NOT DESTRUCTIVE AND NOT A WARNING. Nothing has gone
// wrong and the user has done nothing incorrect -- they opened a demo quote,
// which the app offers them on its own front page. Red or amber would say
// something is broken. What is genuinely blocked (the save) is stated in
// words, and the remedy is stated next to it.

import { Info } from "lucide-react";
import {
  colors, semanticColors, radius, spacing, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export function ExampleDataBanner({
  // The one sentence, already built. Passed in rather than derived here so
  // this banner and the save seams cannot word the same refusal differently
  // -- exampleDataMessage in services/mockQuoteGuard.ts owns it, and owns the
  // rule that it never says "mock", "sample" or "test data".
  message,
}: {
  message: string;
}) {
  if (!message) return null;

  return (
    <div
      data-testid="quote-example-data-banner"
      className="flex items-start"
      style={{
        gap: spacing[8],
        marginTop: spacing[12],
        padding: `${spacing[8]}px ${spacing[12]}px`,
        backgroundColor: colors.indigo50,
        border: `1px solid ${colors.indigo100}`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.sm,
        color: semanticColors.textSecondary,
      }}
    >
      <Info
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        style={{ flexShrink: 0, marginTop: spacing[2], color: semanticColors.accentTextOnTint }}
      />
      <span>
        <strong style={{ color: semanticColors.textPrimary }}>Example quote.</strong>{" "}
        <span data-testid="quote-example-data-detail">{message}</span>
      </span>
    </div>
  );
}
